export const TABLE_TYPE = 'T'
export const ROW_DELIM = '~'
export const CELL_DELIM = ';'
export const CELL_FIELD_DELIM = ':'
const SANITIZE_REGEX = /[\^|~;:]/g

export const sanitizeCellText = (text) => String(text ?? '').replace(SANITIZE_REGEX, '')

export const createEmptyCell = (fontSize) => ({
  content: '',
  isBold: false,
  isItalic: false,
  isUnderline: false,
  alignment: 'left',
  fontSize,
  isSelected: false,
})

export const createEmptyCells = (rows, cols, fontSize) =>
  Array.from({ length: Number(rows) || 0 }, () =>
    Array.from({ length: Number(cols) || 0 }, () => createEmptyCell(fontSize))
  )

const encodeFlags = (cell = {}) => `${cell.isBold ? '1' : '0'}${cell.isItalic ? '1' : '0'}${cell.isUnderline ? '1' : '0'}`

const decodeFlags = (flags = '000') => ({
  isBold: flags[0] === '1',
  isItalic: flags[1] === '1',
  isUnderline: flags[2] === '1',
})

const encodeAlign = (alignment) => {
  if (alignment === 'center') return 'C'
  if (alignment === 'right') return 'R'
  return 'L'
}

const decodeAlign = (code) => {
  if (code === 'C') return 'center'
  if (code === 'R') return 'right'
  return 'left'
}

export const encodeCells = (cells = []) =>
  cells
    .map((row) =>
      (row || [])
        .map(
          (cell) =>
            `${sanitizeCellText(cell.content)}${CELL_FIELD_DELIM}${encodeFlags(cell)}${CELL_FIELD_DELIM}${encodeAlign(cell.alignment)}`
        )
        .join(CELL_DELIM)
    )
    .join(ROW_DELIM)

export const decodeCells = (cellsStr = '', rows, cols, fontSize) => {
  const fallback = createEmptyCells(rows, cols, fontSize)
  if (!cellsStr) return fallback
  const rowParts = cellsStr.split(ROW_DELIM)
  return fallback.map((row, r) =>
    row.map((cell, c) => {
      const raw = rowParts[r]?.split(CELL_DELIM)?.[c]
      if (raw == null) return cell
      const [content = '', flags = '000', align = 'L'] = raw.split(CELL_FIELD_DELIM)
      return {
        ...cell,
        content,
        ...decodeFlags(flags),
        alignment: decodeAlign(align),
        fontSize,
      }
    })
  )
}

export const isTableToken = (token = '') => {
  const parts = String(token).replace(/^\^/, '').split('|')
  return parts[5] === TABLE_TYPE || (parts[0] || '').startsWith('tbl')
}

export const getFormatItemTop = (token = '') => {
  if (!token || token.includes('--THERMAL-PRINT-SPLITTER--')) return null
  const parts = String(token).replace(/^\^/, '').split('|')
  if (isTableToken(token)) return parseInt(parts[3], 10) || 0
  return parseInt(parts[3], 10) || 0
}

export const getThermalSplitTop = (formatTokens = []) => {
  const splitter = formatTokens.find((item) => item?.includes('--THERMAL-PRINT-SPLITTER--'))
  if (!splitter) return 0
  return parseInt(String(splitter).split('|')[3], 10) || 0
}

export const rectsOverlap = (a, b) => {
  if (!a || !b) return false
  return a.left < b.left + b.width && a.left + a.width > b.left && a.top < b.top + b.height && a.top + a.height > b.top
}

const pointInRect = (x, y, rect) =>
  x >= rect.left && x < rect.left + rect.width && y >= rect.top && y < rect.top + rect.height

export const getTablePrintRect = (token) => {
  const table = decodeTable(token)
  return {
    left: table.left || 0,
    top: table.top || 0,
    width: table.width || 0,
    height: table.height || 0,
  }
}

export const getLabelPrintRect = (token = '') => {
  const parts = String(token).replace(/^\^/, '').split('|')
  const value = parts[1] || ''
  const left = parseInt(parts[2], 10) || 0
  const top = parseInt(parts[3], 10) || 0
  const fontPx = (parseFloat(parts[4]) || 10) * (96 / 72)
  const text = String(value).replace(/@/g, '')
  return {
    left,
    top,
    width: Math.max(fontPx, text.length * fontPx * 0.55),
    height: fontPx * 1.3,
  }
}

export const getQrLogoPrintRect = (token = '') => {
  const parts = String(token).replace(/^\^/, '').split('|')
  return {
    left: parseFloat(parts[1]) || 0,
    top: parseFloat(parts[2]) || 0,
    width: parseFloat(parts[4]) || 0,
    height: parseFloat(parts[3]) || 0,
  }
}

const isCoveredByTables = (rect, tableRects = []) =>
  tableRects.some((table) => pointInRect(rect.left, rect.top, table) || rectsOverlap(rect, table))

export const filterItemsHiddenByTables = (tokens = [], tableSourceTokens) => {
  const list = (tokens || []).filter((token) => token)
  const tables = (tableSourceTokens || list).filter((token) => token && isTableToken(token)).map(getTablePrintRect)
  if (!tables.length) return list
  return list.filter((token) => {
    if (isTableToken(token) || String(token).includes('--THERMAL-PRINT-SPLITTER--')) return true
    return !isCoveredByTables(getLabelPrintRect(token), tables)
  })
}

export const filterQrLogoHiddenByTables = (qrLogoTokens = [], formatTokens = []) => {
  const tables = (formatTokens || []).filter((token) => token && isTableToken(token)).map(getTablePrintRect)
  if (!tables.length) return (qrLogoTokens || []).filter((token) => token)
  return (qrLogoTokens || []).filter((token) => token && !isCoveredByTables(getQrLogoPrintRect(token), tables))
}

export const getTopDeductForItem = (token, formatTokens = []) => {
  const splitTop = getThermalSplitTop(formatTokens)
  if (!splitTop) return 0
  const itemTop = getFormatItemTop(token)
  if (itemTop == null) return 0
  return itemTop >= splitTop ? splitTop : 0
}

export const parseColumnWidths = (cellW, cols, fallback = 0) => {
  const count = Number(cols) || 0
  const defaultWidth = parseInt(fallback, 10) || 0
  const parts = String(cellW ?? '')
    .split(',')
    .map((value) => parseInt(value, 10))
    .filter((value) => !Number.isNaN(value) && value > 0)
  if (!parts.length) {
    return Array.from({ length: count }, () => defaultWidth)
  }
  return Array.from({ length: count }, (_, index) => parts[index] || parts[parts.length - 1] || defaultWidth)
}

export const encodeColumnWidths = (columnWidths, cols, fallback = 0) => {
  const widths = parseColumnWidths(
    Array.isArray(columnWidths) ? columnWidths.join(',') : columnWidths,
    cols,
    fallback
  )
  if (!widths.length) return String(parseInt(fallback, 10) || 0)
  const allSame = widths.every((width) => width === widths[0])
  return allSame ? String(widths[0]) : widths.join(',')
}

export const parseRowHeights = parseColumnWidths
export const encodeRowHeights = encodeColumnWidths

export const encodeTable = (item = {}) => {
  const id = String(item.id || '').replace(/^tbl/, '')
  const cols = item.cols
  const rows = item.rows
  const encodedWidths = encodeColumnWidths(item.columnWidths, cols, item.cellWidth)
  const encodedHeights = encodeRowHeights(item.rowHeights, rows, item.cellHeight)
  return `^tbl${id}|TABLE|${Math.round(item.left || 0)}|${Math.round(item.top || 0)}|${item.font || 10}|${TABLE_TYPE}|${rows}|${cols}|${encodedWidths}|${encodedHeights}|${encodeCells(item.cells)}`
}

export const decodeTable = (token = '') => {
  const parts = String(token).replace(/^\^/, '').split('|')
  const [name = '', , left, top, fontSize, , rows, cols, cellW, cellH, ...rest] = parts
  const cellsStr = rest.join('|')
  const id = (name.startsWith('tbl') ? name.slice(3) : name) || Math.random().toString(36).substring(7)
  const parsedRows = parseInt(rows, 10) || 0
  const parsedCols = parseInt(cols, 10) || 0
  const font = parseInt(fontSize, 10) || 10
  const columnWidths = parseColumnWidths(cellW, parsedCols, parseInt(cellW, 10) || 0)
  const rowHeights = parseRowHeights(cellH, parsedRows, parseInt(cellH, 10) || 0)
  const cellWidth = columnWidths[0] || 0
  const cellHeight = rowHeights[0] || 0
  const tableWidth = columnWidths.reduce((sum, width) => sum + width, 0)
  const tableHeight = rowHeights.reduce((sum, height) => sum + height, 0)
  return {
    type: 'table',
    text: 'TABLE',
    id,
    left: parseInt(left, 10) || 0,
    top: parseInt(top, 10) || 0,
    font,
    rows: parsedRows,
    cols: parsedCols,
    cellWidth,
    columnWidths,
    cellHeight,
    rowHeights,
    width: tableWidth,
    height: tableHeight,
    cells: decodeCells(cellsStr, parsedRows, parsedCols, font),
    isBold: 0,
    isItalic: 0,
    isUnderline: 0,
  }
}

export const resolveCellDisplayValue = (text, ticketData = {}) => {
  if (!text) return ''
  return String(text).replace(/@@([^@]+)@/g, (_, key) => {
    const value = ticketData[key]
    return value == null ? '' : String(value)
  })
}

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

export const getTablePrintHtml = (token, ticketData = {}, topDeduct = 0) => {
  const tableItem = typeof token === 'string' ? decodeTable(token) : token
  const top = (tableItem.top || 0) - (topDeduct || 0)
  const columnWidths = parseColumnWidths(tableItem.columnWidths, tableItem.cols, tableItem.cellWidth)
  const rowHeights = parseRowHeights(tableItem.rowHeights, tableItem.rows, tableItem.cellHeight)
  const tableWidth = columnWidths.reduce((sum, width) => sum + width, 0)
  const rowsHtml = (tableItem.cells || [])
    .map((row, rowIndex) => {
      const height = rowHeights[rowIndex] || tableItem.cellHeight
      const tds = (row || [])
        .map((cell, colIndex) => {
          const value = resolveCellDisplayValue(cell.content, ticketData)
          const width = columnWidths[colIndex] || tableItem.cellWidth
          return `<td style="height:${height}px;max-height:${height}px;width:${width}px;border:1px solid #000;padding:0;box-sizing:border-box;overflow:hidden;vertical-align:top;color:#000;"><div style="height:${height}px;max-height:${height}px;overflow:hidden;padding:2px;box-sizing:border-box;font-size:${tableItem.font}pt;font-weight:${cell.isBold ? 'bold' : 'normal'};font-style:${cell.isItalic ? 'italic' : 'normal'};text-decoration:${cell.isUnderline ? 'underline' : 'none'};text-align:${cell.alignment || 'left'};white-space:pre-wrap;overflow-wrap:break-word;">${escapeHtml(value)}</div></td>`
        })
        .join('')
      return `<tr>${tds}</tr>`
    })
    .join('')
  return `<table style="position:absolute;left:calc(${Number(tableItem.left || 0) + 10}px + 2.5mm);top:${top}px;width:${tableWidth}px;border-collapse:collapse;table-layout:fixed;color:#000;font-family:Arial,Helvetica,'Segoe UI',Roboto,sans-serif;">${rowsHtml}</table>`
}
